import React, { useEffect, useMemo, useState } from "react";

import { Download } from "@mui/icons-material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import WebhookOutlinedIcon from "@mui/icons-material/WebhookOutlined";
import { Box, Stack } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";

import {
    faChevronDown,
    faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DEV_MODE } from "Constant/app/process";
import { ButtonVariant } from "Constant/button";
import { addMonths } from "date-fns";
import moment from "moment";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { getDaysTracker } from "helpers/reports/getDaysTracker";
import { getTrackerReports } from "helpers/reports/getTrackerReports";
import { downloadPdf } from "helpers/trackers/downloadPdf";

import PageContainer from "components/atoms/PageContainer";
import StandartButton from "components/atoms/StandartButton";
import { GenericModal } from "components/GenericModal";
import SwalAlertComp from "components/SwalAlert";

import EventErrorTable from "./components/FailedEvents";
import VolumeErrorChart from "./components/VolumeErrorChart";
import EventMatchQualityPopup from "./EventMatchQualityPopup";
import ExpandableRowsComponent from "./ExpandableRowsComponent";
import "./style.css";
import { createTableHeadColumns } from "./TrackerReports.helpers";
import {
    buildDevMockTrackerReportsResponse,
    withDevMockTrackerReports,
} from "./TrackerReports.mock";
import * as Styled from "./TrackerReports.styled";

/**
 * Maps the free form EMQ rating the API returns onto the `.line` colour
 * classes in `style.css`. Kept out of the row markup so the cell stays legible.
 */
const QUALITY_CLASS_BY_RATING = {
    okay: "ok",
    ok: "ok",
    poor: "poor",
    great: "great",
    good: "great",
    "n/a": "na",
};

const qualityClassName = (quality) =>
    QUALITY_CLASS_BY_RATING[
        String(quality ?? "")
            .trim()
            .toLowerCase()
    ] ?? "";

const SortOrder = Object.freeze({
    ASCENDING: "ASCENDING",
    DESCENDING: "DESCENDING",
});

const TrackerReports = () => {
    const params = useParams();
    const navigate = useNavigate();
    const [pixelIds, setPixelIds] = useState([]);
    const [selectedPixelId, setSelectedPixelId] = useState("");
    const [reports, setReports] = useState("");
    const [destinationList, setDestinationList] = useState([]);
    const [sourceCount, setSourceCount] = useState("");

    const [sortOrder, setSortOrder] = useState(SortOrder.DESCENDING);
    const [sortBy, setSortBy] = useState("event");
    const [renderArrayForPixel, setRenderArrayForPixel] = useState([]);
    const [chartData, setChartData] = useState("");
    const [open, setOpen] = useState("");
    const [selectedFilterDate, setSelectedFilterDate] = useState(
        DEV_MODE ? "28" : "7",
    );
    const [renderPixelId, setRenderPixelId] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [trackerDate, setTrackerDate] = useState(null);
    const [show, setShow] = useState(false);
    const currentDate = useMemo(() => new Date(), []);
    const [startDate, setStartDate] = useState(trackerDate);
    const [endDate, setEndDate] = useState(currentDate);
    const [trackerName, setTrackerName] = useState(null);
    const [pdfDocList, setPdfDocList] = useState([]);
    const [pdfDocListModal, setPdfDocListModal] = useState(false);

    const language = useSelector((state) => state.languagesState.languages);

    const sortedTableData = useMemo(() => {
        return renderArrayForPixel?.sort((a, b) => {
            if (
                sortOrder === SortOrder.ASCENDING
                    ? a?.[sortBy] < b?.[sortBy]
                    : a?.[sortBy] > b?.[sortBy]
            ) {
                return -1;
            }
            if (
                sortOrder === SortOrder.ASCENDING
                    ? a?.[sortBy] > b?.[sortBy]
                    : a?.[sortBy] < b?.[sortBy]
            ) {
                return 1;
            }

            // names must be equal
            return 0;
        });
    }, [renderArrayForPixel, sortOrder, sortBy]);

    const handleChangeStart = (date) => {
        setStartDate(date);
        setShow(true);
        if (date > endDate) {
            setEndDate("");
        } else if (date < addMonths(endDate, -6)) {
            const adjustedStartDate = addMonths(endDate, -6);
            setStartDate(adjustedStartDate);
            toast.warn(
                language?.implementationGuide_page?.start_date_6_months ??
                    "Start date cannot be more than 6 months before the end date. It has been automatically adjusted to 6 months.",
            );
        }
    };

    const handleChangeEnd = (date) => {
        setShow(true);
        const sixMonthsLater = addMonths(startDate, 6);
        if (date > sixMonthsLater) {
            date = sixMonthsLater;
            toast.warn(
                language?.implementationGuide_page?.end_date_6_months ??
                    "End date cannot be more than 6 months after the start date. It has been automatically adjusted to 6 months.",
            );
        }
        if (date < startDate) {
            setStartDate(date);
        } else if (date >= startDate) {
            setEndDate(date);
        }
    };

    const columns = [
        {
            name: "Events",
            selector: (row) => row.title && row.title,
        },
        {
            name: <FontAwesomeIcon icon={faExclamationTriangle} />,
        },
        {
            name: "Connection Method",
            selector: (row) => row.browser || row.server,
            sortable: true,
        },
        {
            name: "Success Rate",
            selector: (row) =>
                `% ${Math.round(row.poor && row.poor * 10) / 10}`,
            sortable: true,
        },
        {
            name: "Total Event",
            selector: (row) => row.event,
            sortable: true,
        },
    ];

    useEffect(() => {
        const fetchDataWithDate = async () => {
            if (startDate && endDate && show === true) {
                setIsLoading(true);
                let dates = {
                    tracker_id: params.id,
                    days: 0,
                    start_date: moment(startDate).format("YYYY-MM-DD"),
                    end_date: moment(endDate).format("YYYY-MM-DD"),
                };
                let response = withDevMockTrackerReports(
                    await getDaysTracker(dates),
                    () => buildDevMockTrackerReportsResponse(dates),
                );
                if (response.status) {
                    setIsLoading(false);
                    setReports(response?.data?.reports);
                    const newPixelIds = Object.keys(
                        response?.data?.reports ?? {},
                    );
                    setPixelIds(newPixelIds);

                    // Mevcut seçili pixel yeni listede varsa koru, yoksa ilk geçerli pixel'e fallback yap
                    const validPixelIds = newPixelIds.filter(
                        (id) => id !== "0",
                    );
                    const shouldKeepCurrentPixel =
                        selectedPixelId &&
                        validPixelIds.includes(selectedPixelId);

                    if (!shouldKeepCurrentPixel) {
                        // İlk pixel "0" ise ikinci pixel'i seç, değilse ilk pixel'i seç
                        const fallbackPixelId =
                            newPixelIds[0] === "0"
                                ? newPixelIds[1] || ""
                                : newPixelIds[0] || "";
                        setSelectedPixelId(fallbackPixelId);
                    }

                    setSourceCount(response?.data?.tracker?.sources ?? "");
                    setSelectedFilterDate("");
                    setShow(false);
                } else {
                    SwalAlertComp({
                        title: language?.delete_swal_modal?.failed ?? "Failed",
                        message: response.message,
                        icon: "error",
                    });
                    setShow(false);
                }
            }
        };
        fetchDataWithDate();
    }, [startDate, endDate]);
    useEffect(() => {
        const fetchDataWithDays = async () => {
            if (selectedFilterDate) {
                setIsLoading(true);
                let response = withDevMockTrackerReports(
                    await getTrackerReports(
                        params.id && params.id,
                        selectedFilterDate && selectedFilterDate,
                    ),
                    () =>
                        buildDevMockTrackerReportsResponse({
                            tracker_id: params.id,
                            days: selectedFilterDate,
                        }),
                );
                if (response.status) {
                    setIsLoading(false);
                    setReports(response.data.reports);
                    setDestinationList(
                        response.data.tracker?.destinations.map((x) => ({
                            name: x?.name,
                            destination_code: x?.destination_type?.code,
                            destination_name: x?.destination_type?.name,
                        })),
                    );
                    const newPixelIds = Object.keys(
                        response?.data?.reports ?? {},
                    );
                    setPixelIds(newPixelIds);
                    setTrackerDate(
                        new Date(
                            moment(
                                response.data.tracker?.created_at,
                                "DD-MMM-YYYY HH:mm:ss",
                            ).format("YYYY-MM-DD"),
                        ),
                    );
                    setTrackerName(response.data.tracker?.tracker_name);
                    setStartDate(
                        new Date(
                            moment(
                                response.data.tracker?.created_at,
                                "DD-MMM-YYYY HH:mm:ss",
                            ).format("YYYY-MM-DD"),
                        ),
                    );

                    // Mevcut seçili pixel yeni listede varsa koru, yoksa ilk geçerli pixel'e fallback yap
                    const validPixelIds = newPixelIds.filter(
                        (id) => id !== "0",
                    );
                    const shouldKeepCurrentPixel =
                        selectedPixelId &&
                        validPixelIds.includes(selectedPixelId);

                    if (!shouldKeepCurrentPixel) {
                        // İlk pixel "0" ise ikinci pixel'i seç, değilse ilk pixel'i seç
                        const fallbackPixelId =
                            newPixelIds[0] === "0"
                                ? newPixelIds[1] || ""
                                : newPixelIds[0] || "";
                        setSelectedPixelId(fallbackPixelId);
                    }

                    setSourceCount(response?.data?.tracker?.sources ?? "");

                    if (response.data.tracker?.destinations) {
                        const docList = response.data.tracker.destinations
                            .filter((x) => x?.pdf_available === 1)
                            .map((x) => ({ id: x?.id, name: x?.name }));
                        setPdfDocList(docList);
                        setTimeout(() => {
                            setPdfDocListModal(
                                window.location.href.indexOf(
                                    "open_documents=1",
                                ) > -1 && docList.length > 0,
                            );
                        }, 300);
                    }

                    setStartDate("");
                    setEndDate("");
                } else {
                    SwalAlertComp({
                        title: language?.delete_swal_modal?.failed ?? "Failed",
                        message: response.message,
                        icon: "error",
                    });
                }
            }
        };
        fetchDataWithDays();
    }, [selectedFilterDate]);

    useEffect(() => {
        if (selectedPixelId) {
            // SNGL-2618 EMQ yapısı kullanılabilir hale getirildiğinde bu kod tekrar aktif edilecektir.
            /*
            let eventMatchQualityEvents;
            const fetchEventMatchQuality = async () => {
                const res = await fetchEventMatchQualityAsync(selectedPixelId);
                if (res.status) {
                    eventMatchQualityEvents = res?.data ?? [];
                    changedReportsDataWithOrWithoutEventMatch(
                        eventMatchQualityEvents,
                    );
                } else {
                    changedReportsDataWithOrWithoutEventMatch();
                }
            };

            fetchEventMatchQuality();
            */
            // without EMQ
            changedReportsDataWithOrWithoutEventMatch();
        }
    }, [selectedPixelId, reports]);

    const changedReportsDataWithOrWithoutEventMatch = (eventMatchValue) => {
        let targetKey = selectedPixelId;
        let RenderArray = [];
        let chartRenderData;
        setRenderPixelId(reports[targetKey]);

        for (let subKey in reports[targetKey]) {
            if (subKey == "event_types") {
                let subData = reports[targetKey][subKey];

                for (let subDataKey in subData) {
                    let score;
                    let ratingClone;
                    if (eventMatchValue && eventMatchValue.length > 0) {
                        for (let i = 0; i < eventMatchValue.length; i++) {
                            let indexItem = eventMatchValue[i];
                            if (indexItem["event"] == subDataKey) {
                                score = indexItem.score;
                                ratingClone = indexItem.rating;
                                break;
                            } else {
                                score = "-";
                                ratingClone = indexItem.rating;
                            }
                        }
                    } else {
                        score = "-";
                    }
                    let RenderObject = {
                        title: subDataKey,
                        poor: score,
                        event: subData[subDataKey].sum_data.all_events,
                        rating: ratingClone,
                        errors: subData[subDataKey]?.sum_data?.all_errors || 0,
                    };
                    RenderArray.push(RenderObject);
                }
            } else if (subKey == "sum_data") {
                if (targetKey === "All") {
                    let keys = [...Object.keys(reports)].filter(
                        (x) => x !== "All",
                    );
                    let subDataTotal = reports[targetKey][subKey];

                    chartRenderData = subDataTotal.dates.map((item) => {
                        return {
                            name: item.date,
                            Total: item.all_events,
                            ...(reports[targetKey]?.sum_data?.all_errors
                                ? { errors: item.all_errors }
                                : {}),
                        };
                    });
                    keys.forEach((item) => {
                        reports[item][subKey].dates.forEach((date) => {
                            if (
                                chartRenderData.find(
                                    (x) => x?.name === date?.date,
                                ) !== undefined
                            ) {
                                chartRenderData.find(
                                    (x) => x?.name === date?.date,
                                )[
                                    item +
                                        "|" +
                                        (reports[item]?.destination_type_code ||
                                            "+")
                                ] = date?.all_events;
                            }
                        });
                    });
                } else {
                    let subDataTotal = reports[targetKey][subKey];

                    chartRenderData = subDataTotal.dates.map((item) => {
                        return {
                            name: item.date,
                            Total: item.all_events,
                            ...(reports[targetKey]?.sum_data?.all_errors
                                ? { errors: item.all_errors }
                                : {}),
                        };
                    });
                }
            }
        }

        setRenderArrayForPixel(RenderArray);
        setChartData(chartRenderData);
    };
    const handleChangeFilterDate = (e) => {
        setSelectedFilterDate(e.target.value);
    };

    const handleChangePixelId = (e) => {
        setSelectedPixelId(e.target.value);
    };

    const openCollapseHandler = (element) => {
        if (element === open) {
            setOpen(false);
        } else {
            setOpen(element);
        }
    };

    const dividedNumber = (params) => {
        if (params) {
            let result = params
                .toString()
                .replace(/(\d)(?=(\d{3})+$)/g, "$1" + ".");
            return result;
        }
    };

    const poorFixed = (params) => {
        if (params && params === 100) {
            return params;
        } else {
            let result = params.toFixed(2);
            return result;
        }
    };

    const goTracker = () => {
        navigate(`/tracker/${params.id}`);
    };

    const handleTableHeadColumnClick = ({ accessor }) => {
        if (sortBy === accessor)
            setSortOrder(
                sortOrder === SortOrder.DESCENDING
                    ? SortOrder.ASCENDING
                    : SortOrder.DESCENDING,
            );
        else {
            setSortBy(accessor);
            setSortOrder(SortOrder.DESCENDING);
        }
    };

    const renderHeadColumns = () => {
        const columnNames = createTableHeadColumns({
            language,
        });

        return columnNames.map(({ text, accessor }) => (
            <th key={text}>
                <Styled.SortableHeader
                    as={accessor ? "button" : "span"}
                    type={accessor ? "button" : undefined}
                    style={
                        accessor
                            ? {
                                  background: "none",
                                  border: "none",
                                  padding: 0,
                                  font: "inherit",
                                  color: "inherit",
                                  letterSpacing: "inherit",
                                  textTransform: "inherit",
                              }
                            : { cursor: "default" }
                    }
                    onClick={() =>
                        accessor && handleTableHeadColumnClick({ accessor })
                    }
                >
                    {text}
                    {accessor && sortBy === accessor && (
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            style={{
                                transform:
                                    sortOrder === SortOrder.ASCENDING
                                        ? "rotate(180deg)"
                                        : "rotate(360deg)",
                                transition: "all 200ms",
                            }}
                        />
                    )}
                </Styled.SortableHeader>
            </th>
        ));
    };

    const openPdfDoc = async (id) => {
        let response = await downloadPdf(id);
        if (response?.status) {
            let fileContent = response?.data?.[0]?.data;
            let fileName = response?.data?.[0]?.filename;
            const binaryString = window.atob(fileContent);
            const len = binaryString.length;
            const bytes = new Uint8Array(len);
            for (let i = 0; i < len; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: "application/pdf" });
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
        }
    };

    const handlePdfDocListModalClose = () => {
        setPdfDocListModal(false);
    };

    return (
        <PageContainer
            containerClassName="tracker-detail-area"
            title={trackerName}
            subtitle={
                language?.tracker_detail?.tracker_reports_desc ??
                "On this page, you can view tracker report."
            }
            onBack={() => navigate(-1)}
            additionalButtons={
                <Stack direction="row" gap={1.5} alignItems="center">
                    <StandartButton
                        onClick={goTracker}
                        variant={ButtonVariant.BLACK_OUTLINED}
                    >
                        {language?.trackers?.go_tracker ?? "Go Tracker"}
                    </StandartButton>
                    {!!pdfDocList?.length && (
                        <StandartButton
                            onClick={() => setPdfDocListModal(true)}
                            className="black-outlined icon-button"
                        >
                            <Stack direction="row" gap={1} alignItems="center">
                                <PictureAsPdfOutlinedIcon />
                                <Box>
                                    {language?.tracker_detail?.ai_report ??
                                        "AI Report"}
                                </Box>
                            </Stack>
                        </StandartButton>
                    )}
                </Stack>
            }
        >
            <Box>
                <Styled.FilterCard elevation={0}>
                    <Styled.FilterRow>
                        <Styled.FilterField $area="start">
                            <Styled.FilterLabel>
                                {language?.tracker_detail?.start_date ??
                                    "Start Date"}
                            </Styled.FilterLabel>
                            {isLoading ? (
                                <Styled.SkeletonBlock $h={40} />
                            ) : (
                                <Styled.DateInput className="mui-datepicker">
                                    <ReactDatePicker
                                        className="datePicker"
                                        selected={startDate}
                                        selectsStart
                                        placeholderText="dd/mm/yyyy"
                                        onChange={handleChangeStart}
                                        dateFormat="dd/MM/yyyy"
                                        minDate={trackerDate}
                                        maxDate={currentDate}
                                    />
                                    <DateRangeOutlinedIcon />
                                </Styled.DateInput>
                            )}
                        </Styled.FilterField>

                        <Styled.FilterField $area="end">
                            <Styled.FilterLabel>
                                {language?.tracker_detail?.end_date ??
                                    "End Date"}
                            </Styled.FilterLabel>
                            {isLoading ? (
                                <Styled.SkeletonBlock $h={40} />
                            ) : (
                                <Styled.DateInput className="mui-datepicker">
                                    <ReactDatePicker
                                        className="datePicker"
                                        selected={endDate}
                                        selectsEnd
                                        placeholderText="dd/mm/yyyy"
                                        onChange={handleChangeEnd}
                                        dateFormat="dd/MM/yyyy"
                                        minDate={startDate}
                                        maxDate={currentDate}
                                    />
                                    <DateRangeOutlinedIcon />
                                </Styled.DateInput>
                            )}
                        </Styled.FilterField>

                        <Styled.OrDivider>
                            {language?.tracker_detail?.or ?? "OR"}
                        </Styled.OrDivider>

                        <Styled.FilterField $area="range">
                            <Styled.FilterLabel>
                                {language?.tracker_detail?.date_range ??
                                    "Date Range"}
                            </Styled.FilterLabel>
                            <Styled.StyledFormControl size="small">
                                <Select
                                    onChange={handleChangeFilterDate}
                                    value={selectedFilterDate}
                                    displayEmpty
                                >
                                    <MenuItem key="2" value="2">
                                        {language?.reports?.yesterday ??
                                            "Yesterday"}
                                    </MenuItem>
                                    <MenuItem key="1" value="1">
                                        {language?.reports?.today ?? "Today"}
                                    </MenuItem>
                                    <MenuItem key="7" value="7">
                                        {language?.reports?.last_7_days ??
                                            "Last 7 days"}
                                    </MenuItem>
                                    <MenuItem key="28" value="28">
                                        {language?.reports?.last_28_days ??
                                            "Last 28 days"}
                                    </MenuItem>
                                    <MenuItem key="90" value="90">
                                        {language?.reports?.last_90_days ??
                                            "Last 90 days"}
                                    </MenuItem>
                                </Select>
                            </Styled.StyledFormControl>
                        </Styled.FilterField>

                        <Styled.FilterField $area="pixel">
                            <Styled.FilterLabel>
                                {language?.tracker_detail?.pixel ?? "Pixel"}
                            </Styled.FilterLabel>
                            <Styled.StyledFormControl size="small">
                                <Select
                                    onChange={handleChangePixelId}
                                    value={selectedPixelId}
                                    displayEmpty
                                >
                                    {pixelIds.length > 0 &&
                                        pixelIds.map(
                                            (item) =>
                                                +item !== 0 && (
                                                    <MenuItem
                                                        key={item}
                                                        value={item}
                                                    >
                                                        {item}
                                                    </MenuItem>
                                                ),
                                        )}
                                </Select>
                            </Styled.StyledFormControl>
                        </Styled.FilterField>
                    </Styled.FilterRow>
                </Styled.FilterCard>
            </Box>

            <Box mt={3}>
                <Styled.MetricsRow>
                    {isLoading ? (
                        [0, 1].map((i) => (
                            <Styled.MetricCard
                                key={`metric-skeleton-${i}`}
                                elevation={0}
                            >
                                <Styled.SkeletonBlock $h={14} />
                                <Styled.SkeletonBlock $h={36} />
                            </Styled.MetricCard>
                        ))
                    ) : (
                        <>
                            <Styled.MetricCard elevation={0}>
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Styled.MetricLabel>
                                        {language?.tracker_detail
                                            ?.total_events ?? "Total Events"}
                                    </Styled.MetricLabel>
                                    <Styled.IconBadge
                                        $bg="rgba(0,0,0,0.08)"
                                        $color="#000000"
                                    >
                                        <WebhookOutlinedIcon
                                            sx={{ fontSize: 20 }}
                                        />
                                    </Styled.IconBadge>
                                </Stack>
                                <Styled.MetricValue>
                                    {dividedNumber(
                                        renderPixelId?.sum_data?.all_events,
                                    ) || 0}
                                </Styled.MetricValue>
                            </Styled.MetricCard>
                            <Styled.MetricCard elevation={0}>
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Styled.MetricLabel>
                                        {language?.tracker_detail
                                            ?.total_errors ?? "Total Errors"}
                                    </Styled.MetricLabel>
                                    <Styled.IconBadge
                                        $bg="rgba(0,0,0,0.08)"
                                        $color="#000000"
                                    >
                                        <ErrorOutlineIcon
                                            sx={{ fontSize: 20 }}
                                        />
                                    </Styled.IconBadge>
                                </Stack>
                                <Styled.MetricValue>
                                    {dividedNumber(
                                        renderPixelId?.sum_data?.all_errors,
                                    ) || 0}
                                </Styled.MetricValue>
                            </Styled.MetricCard>
                        </>
                    )}
                </Styled.MetricsRow>
            </Box>

            <Box mt={3}>
                <Styled.SectionCard elevation={0}>
                    <Styled.SectionHeader>
                        <Box>
                            <Styled.SectionTitle>
                                {language?.tracker_detail?.event_volume ??
                                    "Event Volume & Errors"}
                            </Styled.SectionTitle>
                            <Styled.SectionSubtitle>
                                {language?.tracker_detail?.event_volume_desc ??
                                    "Events delivered per destination over the selected range."}
                            </Styled.SectionSubtitle>
                        </Box>
                    </Styled.SectionHeader>
                    <VolumeErrorChart
                        chartData={chartData}
                        destinationList={destinationList}
                        loader={isLoading}
                    />
                </Styled.SectionCard>
            </Box>

            {sortedTableData.length > 0 && (
                <Box mt={3}>
                    <Styled.SectionCard elevation={0} $flush>
                        <Styled.SectionHeader>
                            <Box>
                                <Styled.SectionTitle>
                                    {language?.reports?.events ?? "Events"}
                                </Styled.SectionTitle>
                                <Styled.SectionSubtitle>
                                    {`${sortedTableData.length} event ${
                                        sortedTableData.length === 1
                                            ? "type"
                                            : "types"
                                    } tracked`}
                                </Styled.SectionSubtitle>
                            </Box>
                        </Styled.SectionHeader>
                        <Styled.TableWrap>
                            <Styled.ReportTable>
                                <thead>
                                    <tr>
                                        <th style={{ width: 56 }} />
                                        {renderHeadColumns()}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedTableData.map((item) => {
                                        const isOpen = open === item?.title;
                                        return (
                                            <React.Fragment key={item.title}>
                                                <tr
                                                    className={`report-row${
                                                        isOpen ? " is-open" : ""
                                                    }`}
                                                    onClick={() =>
                                                        openCollapseHandler(
                                                            item.title,
                                                        )
                                                    }
                                                >
                                                    <td>
                                                        <Styled.ExpandToggle
                                                            $open={isOpen}
                                                            aria-expanded={
                                                                isOpen
                                                            }
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={
                                                                    faChevronDown
                                                                }
                                                            />
                                                        </Styled.ExpandToggle>
                                                    </td>
                                                    <td>
                                                        <Styled.EventName
                                                            title={item.title}
                                                        >
                                                            {item.title}
                                                        </Styled.EventName>
                                                    </td>
                                                    <td>
                                                        <div
                                                            className={
                                                                "line " +
                                                                qualityClassName(
                                                                    item.poor,
                                                                )
                                                            }
                                                        >
                                                            <EventMatchQualityPopup
                                                                quality={
                                                                    item.poor
                                                                }
                                                                eventName={
                                                                    item.title
                                                                }
                                                                pixelId={
                                                                    selectedPixelId
                                                                }
                                                                rating={
                                                                    item.rating
                                                                }
                                                            />
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <Styled.NumericCell>
                                                            {dividedNumber(
                                                                item.event,
                                                            ) || 0}
                                                        </Styled.NumericCell>
                                                    </td>
                                                    <td>
                                                        {item.errors > 0 ? (
                                                            <Styled.StatusChip
                                                                $bg="rgba(220, 38, 38, 0.1)"
                                                                $color="#DC2626"
                                                            >
                                                                <ErrorOutlineIcon
                                                                    sx={{
                                                                        fontSize: 13,
                                                                    }}
                                                                />
                                                                {dividedNumber(
                                                                    item.errors,
                                                                )}
                                                            </Styled.StatusChip>
                                                        ) : (
                                                            <Styled.NumericCell>
                                                                0
                                                            </Styled.NumericCell>
                                                        )}
                                                    </td>
                                                </tr>
                                                {isOpen && (
                                                    <tr className="report-detail">
                                                        <td colSpan={5}>
                                                            <ExpandableRowsComponent
                                                                title={
                                                                    item?.title ??
                                                                    ""
                                                                }
                                                                renderPixelIdProps={
                                                                    renderPixelId
                                                                }
                                                            />
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </Styled.ReportTable>
                        </Styled.TableWrap>
                    </Styled.SectionCard>
                </Box>
            )}
            <GenericModal
                open={pdfDocListModal}
                onClose={handlePdfDocListModalClose}
                title={language?.tracker_detail?.report_list ?? "Report List"}
                subtitle={
                    language?.tracker_detail?.report_list_subtitle ??
                    `${pdfDocList.length} report${
                        pdfDocList.length === 1 ? "" : "s"
                    } available`
                }
                icon={
                    <ArticleOutlinedIcon
                        sx={{ color: "#ed6c02", fontSize: 28 }}
                    />
                }
                maxWidth="sm"
                contentPadding={0}
                content={
                    <Styled.PdfDocList>
                        {pdfDocList.map((item) => (
                            <Styled.PdfDocListItem
                                key={item.id}
                                variant="outlined"
                                onClick={() => openPdfDoc(item?.id)}
                            >
                                <Styled.PdfDocListItemName>
                                    {item?.name}
                                </Styled.PdfDocListItemName>
                                <Styled.PdfDocListItemIcon>
                                    <Download fontSize="small" />
                                </Styled.PdfDocListItemIcon>
                            </Styled.PdfDocListItem>
                        ))}
                    </Styled.PdfDocList>
                }
            />
            <EventErrorTable
                trackerId={params.id}
                endDate={endDate}
                startDate={startDate}
                selectedFilterDate={selectedFilterDate}
                show={show}
            />
        </PageContainer>
    );
};

export default TrackerReports;
